/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloudIcon, Type, Sparkles } from './icons';
import { modalApi } from '../services/modalApi';
import Spinner from './Spinner';
import { getFriendlyErrorMessage, renderLogoToImage } from '../lib/utils';
import { compressAndResizeImage } from '../lib/imageUtils';
import { LOADING_CONFIG } from '../lib/loadingConfig';
import { indexedDBService } from '../services/indexedDbService';

interface StartScreenProps {
  onDesignReady: (data: { imageId: string; filepath: string; previewUrl: string }) => void;
  title?: string;
  description?: string;
  mode?: 'phone' | 'cup'; // Phone: simple upload, Cup: upload + text generation
}

type TabMode = 'upload' | 'text';

const StartScreen: React.FC<StartScreenProps> = ({
  onDesignReady,
  title = "Thiết Kế In Logo Cốc Bã Cà Phê",
  description = "Upload logo hoặc tạo logo từ chữ. AI sẽ tạo mockup in laser màu vàng trên cốc bã cà phê.",
  mode = 'cup' // Default to cup for backward compatibility
}) => {
  const [activeTab, setActiveTab] = useState<TabMode>('upload');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Tips rotation state
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  // Text input state
  const [textInput, setTextInput] = useState('');
  const [fontStyle, setFontStyle] = useState('bold');
  const [textAlign, setTextAlign] = useState<'center' | 'left' | 'right'>('center');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const tips = LOADING_CONFIG.upload[mode === 'phone' ? 'phone' : 'cup'];

  // Rotate tips when processing
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      interval = setInterval(() => {
        setCurrentTipIndex((prev) => (prev + 1) % tips.length);
      }, 3000); 
    }
    return () => clearInterval(interval);
  }, [isProcessing, tips.length]);

  // Auto-preview when font style or text alignment changes
  useEffect(() => {
    if (textInput.trim() && logoPreview && !isProcessing) {
      // Regenerate preview with new settings
      const updatePreview = async () => {
        try {
          const logoBase64 = renderLogoToImage(textInput.trim(), fontStyle, textAlign);
          setLogoPreview(logoBase64);
        } catch (err) {
          console.warn('[StartScreen] Failed to update preview:', err);
        }
      };
      updatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontStyle, textAlign]); // Only trigger on font/alignment change

  // Handle logo generation from text (frontend canvas rendering)
  const handleGenerateLogoFromText = useCallback(async () => {
    const generateStartTime = performance.now();
    console.log('[StartScreen] ============================================');
    console.log('[StartScreen] Generate logo from text (FRONTEND):', {
      text: textInput.substring(0, 50) + (textInput.length > 50 ? '...' : ''),
      fontStyle,
      textAlign,
      color: '#000000 (Black)',
      timestamp: new Date().toISOString()
    });

    if (!textInput.trim()) {
      console.error('[StartScreen] ❌ Empty text input');
      setError('Vui lòng nhập chữ để tạo logo.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      console.log('[StartScreen] 🎨 Rendering logo with canvas (black #000000)...');
      const renderStartTime = performance.now();

      // Generate logo locally using canvas with black color #000000
      const logoBase64 = renderLogoToImage(textInput.trim(), fontStyle, textAlign);

      const renderDuration = performance.now() - renderStartTime;
      console.log(`[StartScreen] ✅ Logo rendered in ${renderDuration.toFixed(2)}ms`, {
        imageSize: `${(logoBase64.length / 1024).toFixed(2)}KB`
      });

      if (!logoBase64) {
        throw new Error('Failed to render logo');
      }

      setLogoPreview(logoBase64);

      // Save to IndexedDB instead of uploading immediately (lazy upload optimization)
      console.log('[StartScreen] 💾 Saving logo to IndexedDB...');
      const saveStartTime = performance.now();

      const tempId = crypto.randomUUID();
      await indexedDBService.saveDesignImage(tempId, logoBase64);

      const saveDuration = performance.now() - saveStartTime;
      console.log(`[StartScreen] ✅ Logo saved to IndexedDB in ${saveDuration.toFixed(2)}ms`);

      const totalDuration = performance.now() - generateStartTime;
      console.log(`[StartScreen] ✅ Logo generation complete in ${totalDuration.toFixed(2)}ms:`, {
        tempId: tempId.substring(0, 8) + '...',
        note: 'Upload deferred until mockup generation'
      });

      // Return temp ID - will be uploaded later when needed
      onDesignReady({
        imageId: tempId,
        filepath: '', // Will be set after upload
        previewUrl: logoBase64
      });
      console.log('[StartScreen] ============================================');
    } catch (err) {
      const totalDuration = performance.now() - generateStartTime;
      console.error(`[StartScreen] ❌ Logo generation failed after ${totalDuration.toFixed(2)}ms:`, {
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined
      });
      setError(getFriendlyErrorMessage(err, 'Tạo logo từ chữ thất bại'));
      console.log('[StartScreen] ============================================');
    } finally {
      setIsProcessing(false);
    }
  }, [textInput, fontStyle, textAlign, onDesignReady]);

  // Handle file upload
  const handleFileSelect = useCallback(async (file: File) => {
    const uploadStartTime = performance.now();
    console.log('[StartScreen] ============================================');
    console.log('[StartScreen] File selected by user:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)}KB`,
      lastModified: new Date(file.lastModified).toISOString(),
      timestamp: new Date().toISOString()
    });

    if (!file.type.startsWith('image/')) {
        console.error('[StartScreen] ❌ Invalid file type:', file.type);
        setError('Vui lòng chọn tệp hình ảnh.');
        return;
    }

    setIsProcessing(true);
    setError(null);
    try {
        console.log('[StartScreen] 🔄 Compressing and resizing image client-side (Print Safe)...');
        const processingStartTime = performance.now();

        // Use high-quality settings for printing (2500px, 0.95 quality)
        const base64String = await compressAndResizeImage(file, 2500, 2500, 0.95);

        const processingDuration = performance.now() - processingStartTime;
        const originalSize = file.size;
        const compressedSizeApprox = Math.round((base64String.length * 3) / 4);
        const compressionRatio = ((1 - (compressedSizeApprox / originalSize)) * 100).toFixed(1);

        console.log(`[StartScreen] ✅ Image processed in ${processingDuration.toFixed(2)}ms:`, {
          originalSize: `${(originalSize / 1024).toFixed(2)}KB`,
          newSize: `${(compressedSizeApprox / 1024).toFixed(2)}KB`,
          ratio: `${compressionRatio}% reduction`
        });

        setLogoPreview(base64String);

        // Save to IndexedDB instead of uploading immediately (lazy upload optimization)
        console.log('[StartScreen] 💾 Saving image to IndexedDB...');
        const saveStartTime = performance.now();

        const tempId = crypto.randomUUID();
        await indexedDBService.saveDesignImage(tempId, base64String);

        const saveDuration = performance.now() - saveStartTime;
        console.log(`[StartScreen] ✅ Image saved to IndexedDB in ${saveDuration.toFixed(2)}ms`);

        const totalDuration = performance.now() - uploadStartTime;
        console.log(`[StartScreen] ✅ Upload flow complete in ${totalDuration.toFixed(2)}ms:`, {
          tempId: tempId.substring(0, 8) + '...',
          note: 'Upload deferred until mockup generation'
        });

        // Return temp ID - will be uploaded later when needed
        onDesignReady({
            imageId: tempId,
            filepath: '', // Will be set after upload
            previewUrl: base64String
        });
        console.log('[StartScreen] ============================================');
    } catch (err) {
        const totalDuration = performance.now() - uploadStartTime;
        console.error(`[StartScreen] ❌ Processing/Save failed after ${totalDuration.toFixed(2)}ms:`, err);
        setError(getFriendlyErrorMessage(err, 'Lưu ảnh thất bại'));
        console.log('[StartScreen] ============================================');
    } finally {
        setIsProcessing(false);
    }
  }, [onDesignReady]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const screenVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  };

  return (
    <motion.div
      key="uploader"
      className="w-full max-w-3xl mx-auto flex flex-col items-center justify-center gap-6 px-4 py-4"
      variants={screenVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Header */}
      <div className="w-full text-center space-y-4">
          <h1 className="text-2xl md:text-4xl font-semibold text-slate-900 leading-tight">
            {title}
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-md mx-auto">
            {description}
          </p>
      </div>

      {/* Tab Selector - Only for Cup mode */}
      {mode === 'cup' && (
        <div className="w-full max-w-md flex gap-2 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => {
              setActiveTab('upload');
              setError(null);
              setLogoPreview(null);
            }}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'upload'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <UploadCloudIcon className="w-4 h-4 inline mr-2" />
            Upload Logo
          </button>
          <button
            onClick={() => {
              setActiveTab('text');
              setError(null);
              setLogoPreview(null);
            }}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'text'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Type className="w-4 h-4 inline mr-2" />
            Tạo từ Chữ
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div className="w-full max-w-md space-y-4">
        <AnimatePresence mode="wait">
          {(mode === 'phone' || activeTab === 'upload') ? (
            <motion.div
              key="upload-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {isProcessing ? (
                // LOADING STATE UI
                <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center space-y-4 min-h-[200px]">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
                        {logoPreview && (
                             <div className="absolute inset-0 flex items-center justify-center">
                                <img src={logoPreview} alt="Uploading" className="w-8 h-8 object-cover rounded-full opacity-50" />
                             </div>
                        )}
                    </div>
                    
                    <div className="text-center space-y-2 max-w-xs">
                        <h3 className="text-slate-900 font-medium text-lg">Đang xử lý ảnh...</h3>
                        <div className="h-10 overflow-hidden">
                            <AnimatePresence mode="wait">
                                <motion.p 
                                    key={currentTipIndex}
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -5 }}
                                    className="text-sm text-slate-600"
                                >
                                    {tips[currentTipIndex]}
                                </motion.p>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
              ) : (
                // NORMAL UPLOAD UI
                <>
                    <label
                        htmlFor="image-upload-start"
                        className={`w-full relative flex items-center justify-center px-6 py-4 text-base font-medium text-white ${
                        mode === 'phone'
                            ? 'bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900'
                            : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800'
                        } rounded-lg cursor-pointer transition-all shadow-sm active:scale-98 min-h-[44px]`}
                    >
                        <UploadCloudIcon className="w-5 h-5 mr-2 flex-shrink-0" />
                        <span>{mode === 'phone' ? 'Chọn Hình Ảnh' : 'Chọn File Logo'}</span>
                    </label>
                    <input
                        id="image-upload-start"
                        type="file"
                        className="hidden"
                        accept="image/png, image/jpeg, image/webp, image/gif"
                        onChange={handleFileChange}
                        disabled={isProcessing}
                    />
                    {mode === 'phone' ? (
                        <div className="flex flex-col gap-2">
                             <p className="text-xs text-slate-600 text-center">
                                Hỗ trợ PNG, JPEG, GIF, WebP (Tối ưu in ấn)
                            </p>
                            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 bg-slate-50 p-2 rounded-lg border border-slate-100">
                                <Sparkles className="w-3 h-3 text-amber-500" />
                                <span>AI hỗ trợ tách nền & làm nét ảnh tự động</span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-xs text-emerald-700 text-center">
                        💡 Khuyến nghị: PNG trong suốt để kết quả tốt nhất
                        </p>
                    )}
                </>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="text-tab"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Text Input - Multiline Support */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nhập chữ cho logo
                </label>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Ví dụ: Sun Life&#10;BỘ QUỐC PHÒNG&#10;(Nhấn Enter để xuống dòng)"
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                  disabled={isProcessing}
                />
                <p className="text-xs text-slate-500 mt-1">
                  💡 Nhấn Enter để xuống dòng
                </p>
              </div>

              {/* Font Style */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kiểu chữ
                </label>
                <select
                  value={fontStyle}
                  onChange={(e) => setFontStyle(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={isProcessing}
                >
                  <option value="bold">Đậm</option>
                  <option value="regular">Thường</option>
                  <option value="elegant">Thanh lịch</option>
                  <option value="modern">Hiện đại</option>
                </select>
              </div>

              {/* Text Alignment */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Căn chỉnh
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setTextAlign('left')}
                    disabled={isProcessing}
                    className={`px-3 py-3 border rounded-lg font-medium transition-all text-sm ${
                      textAlign === 'left'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-500'
                    }`}
                  >
                    ⬅️ Trái
                  </button>
                  <button
                    type="button"
                    onClick={() => setTextAlign('center')}
                    disabled={isProcessing}
                    className={`px-3 py-3 border rounded-lg font-medium transition-all text-sm ${
                      textAlign === 'center'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-500'
                    }`}
                  >
                    ⬛ Giữa
                  </button>
                  <button
                    type="button"
                    onClick={() => setTextAlign('right')}
                    disabled={isProcessing}
                    className={`px-3 py-3 border rounded-lg font-medium transition-all text-sm ${
                      textAlign === 'right'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-500'
                    }`}
                  >
                    ➡️ Phải
                  </button>
                </div>
              </div>

              {/* Color Info */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs text-emerald-800">
                  🎨 Logo màu <strong>đen đậm</strong>, tự động chuyển sang <strong>màu vàng/gold</strong> khi in laser lên cốc
                </p>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateLogoFromText}
                disabled={isProcessing || !textInput.trim()}
                className={`w-full px-6 py-4 text-base font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 rounded-lg transition-all shadow-sm active:scale-98 min-h-[44px] ${
                  isProcessing || !textInput.trim() ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center">
                    <Spinner />
                    <span className="ml-2">Đang tạo logo...</span>
                  </span>
                ) : (
                  '✨ Tạo Logo'
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Logo Preview */}
        {!isProcessing && logoPreview && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full bg-white rounded-lg border-2 border-emerald-200 p-4 shadow-sm"
          >
            <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <span className="text-emerald-600">👁️</span>
              Preview Logo:
            </p>
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-6 flex items-center justify-center min-h-[150px] border border-slate-200">
              <img
                src={logoPreview}
                alt="Logo preview"
                className="max-w-full max-h-40 object-contain"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Logo sẽ tự động chuyển sang màu vàng/gold khi in lên cốc
            </p>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <div className="w-full bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200 animate-fade-in">
            {error}
          </div>
        )}

        {/* Info Text */}
        {!isProcessing && (
            <p className="text-slate-500 text-xs text-center">
            {activeTab === 'upload' 
                ? 'Hệ thống tự động điều chỉnh ảnh để in đẹp nhất'
                : 'Logo sẽ được tạo với nền trong suốt, màu vàng/gold phù hợp in laser'}
            </p>
        )}
      </div>
    </motion.div>
  );
};

export default StartScreen;